import { useEffect, useMemo, useState } from "preact/hooks";
import { CONFETTI_FRAMES, CONFETTI_FRAME_MS, confettiAt, confettiPieces } from "../../shared/cheers.ts";

/**
 * A burst of pixel confetti over the whole screen (docs/cheers.md "The confetti"; shared/cheers.ts
 * confettiPieces): square pieces in the popper's colours and the app's gold, falling in hard frames
 * (docs/feel.md: motion like a sprite, never eased), placed in whole px of the measured window. It
 * takes no touch and clears itself; `onDone` runs once the last frame has gone. Nothing with reduced
 * motion, `onDone` at once. A new `seed` throws a new burst.
 */
export function Confetti({ seed, onDone }: { seed: number; onDone?: () => void }) {
  const reduced = typeof window !== "undefined" && (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false);
  const pieces = useMemo(() => confettiPieces(seed), [seed]);
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    setFrame(0);
    if (reduced) {
      onDone?.();
      return;
    }
    let f = 0;
    const timer = setInterval(() => {
      f += 1;
      if (f >= CONFETTI_FRAMES) {
        clearInterval(timer);
        onDone?.();
      }
      setFrame(f);
    }, CONFETTI_FRAME_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);
  if (reduced || frame >= CONFETTI_FRAMES) return null;
  const w = window.innerWidth;
  const h = window.innerHeight;
  return (
    <div class="confetti" aria-hidden="true">
      {pieces.map((p, i) => {
        const at = confettiAt(p, frame, w, h);
        return at ? <i key={i} style={{ left: `${at.left}px`, top: `${at.top}px`, width: `${p.size}px`, height: `${p.size}px`, background: p.fill }} /> : null;
      })}
    </div>
  );
}
